/* eslint-disable camelcase */
import { Logger } from '@deqo/foundation';
import posthog from 'posthog-js';
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Host } from '../Host';

const debug = Logger.get('app.telemetry');
const urlParams = new URLSearchParams(window.location.search);
const enabled = Host.siteId && urlParams.get('ui') !== '0';

const init = () => {
    window.addEventListener('click', onWindowClick, { passive: true, capture: true });

    if (!enabled) return;
    posthog.init(Host.siteId, {
        api_host: 'https://eu.posthog.com',
        autocapture: false,
        capture_pageview: false,
        disable_session_recording: true,
    });
};

const onWindowClick = (e: MouseEvent) => {
    if (e.target instanceof HTMLElement) {
        const eventName = findEventAtTarget(e.target);
        if (eventName) {
            emit(eventName, collectContextData(e.target));
        }
    }
};

const login = (userId: string, name: string, email: string, accountId: string, accountName: string) => {
    debug.log(`Login: ${userId}, ${name}, ${email}, ${accountId}, ${accountName}`);
    if (!enabled) return;
    posthog.identify(userId, { name, email });
    posthog.group('account', accountId, { name: accountName });
};

const emit = (event: string, data?: Record<string, unknown>) => {
    debug.log(`Emit: ${event} -> ${JSON.stringify(data)}`);
    if (!enabled) return;
    posthog.capture(event, data ?? {});
};

const debounceMap = new Map<string, any>();

const emitDebounced = (event: string, data?: Record<string, unknown>, delayMs = 500) => {
    if (debounceMap.has(event)) {
        clearTimeout(debounceMap.get(event));
    }

    const tid = setTimeout(() => {
        emit(event, data);
        debounceMap.delete(event);
    }, delayMs);

    debounceMap.set(event, tid);
};

const pageView = () => {
    debug.log('Emit: $pageview');
    if (!enabled) return;
    posthog.capture('$pageview');
};

export const telemetry = Object.freeze({ enabled, init, login, emit, emitDebounced, pageView });

const findEventAtTarget = (elmt: HTMLElement) => {
    // Walk up the dom tree and look for data-action attribute
    while (elmt) {
        if (elmt.dataset?.action) {
            return elmt.dataset.action;
        }
        elmt = elmt.parentElement;
    }
    return null;
};

const collectContextData = (elmt: HTMLElement) => {
    // Walk up the dom tree and collect tdata
    const data = {} as Record<string, unknown>;
    while (elmt) {
        if (elmt.dataset?.tdata) {
            Object.assign(data, JSON.parse(elmt.dataset.tdata));
        }
        elmt = elmt.parentElement;
    }
    return data;
};

const parseEventName = (eventNameSyntax: string): { eventName: string, mappings: Record<string, number> } => {
    const match = eventNameSyntax.match(/^([^[]+)(?:\[([^\]]+)\])?$/);
    if (match) {
        const [_, eventName, mappingsStr] = match;

        if (mappingsStr) {
            const mappingKeys = mappingsStr.split(',').map(key => key.trim());
            const mappings = Object.fromEntries(mappingKeys.map((key, index) => [key, index]));

            return { eventName, mappings };
        }
        else {
            return { eventName, mappings: {} };
        }
    }
    else {
        return { eventName: eventNameSyntax, mappings: {} };
    }
};

export const useTracked = <T extends (...args: any[]) => any>(eventNameWithMappings: string, func: T) => {
    const { eventName, mappings } = useMemo(() => parseEventName(eventNameWithMappings), [eventNameWithMappings]);
    const contextData = useContext(TelemetryContextInternal);

    return useCallback(function (this: any, ...args: Parameters<T>): ReturnType<T> {

        // Add mapped arguments to the event data
        const eventData = { ...contextData };
        for (const [key, argIndex] of Object.entries(mappings)) {
            if (argIndex < args.length) {
                eventData[key] = args[argIndex];
            }
        }

        telemetry.emit(eventName, eventData);
        return func.apply(this, args);
    }, [contextData, func]);
};

const TelemetryContextInternal = React.createContext({} as Record<string, unknown>);

export const TelemetryContext = ({ children, ...props }: { children: React.ReactNode } & Record<string, unknown>) => {

    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (ref?.current?.dataset) {
            ref.current.dataset.tdata = JSON.stringify(props);
        }
    }, [props, ref.current]);

    const parentProps = useContext(TelemetryContextInternal);

    return <TelemetryContextInternal.Provider value={{ ...parentProps, ...props }}>
        {children}
    </TelemetryContextInternal.Provider>;
};
